/**
 * useLiveElapsed (NC-1294)
 *
 * Elapsed milliseconds since `startedAtUtc`, re-evaluated on a timer while
 * `isLive` is true. Same shape and the same hard-won reason as
 * `useExpiringLock` (NC-1258): a `useMemo` over `Date.now()` freezes at first
 * evaluation, so a "läuft seit …" value computed that way never moves — the run
 * document does not change while the run is running, so nothing invalidates the
 * memo and no parent poll helps.
 *
 * `isLive` comes straight from the API (`runtime.isLive`, whose contract says
 * "the UI must not tick a clock when this is false"). No timer is armed for a
 * finished, scheduled or unknown run — those get their duration from the
 * server's `elapsedMs` instead.
 *
 * Ticks at minute resolution because the display is minute-resolution
 * (formatDuration only shows seconds below one minute). A one-second timer would
 * re-render 60x more often for no visible difference.
 */

import { useEffect, useState } from 'react';

/** Deliberately coarse — see the note above. */
export const LIVE_ELAPSED_TICK_MS = 30_000;

export function useLiveElapsed(
  startedAtUtc: string | undefined,
  isLive: boolean
): number | null {
  const [now, setNow] = useState(() => Date.now());

  const startedMs = startedAtUtc ? Date.parse(startedAtUtc) : NaN;
  const valid = Number.isFinite(startedMs);

  useEffect(() => {
    // Not live, or nothing parsable to count from: never arm a timer.
    if (!isLive || !valid) return undefined;

    // Re-read the clock immediately: the component may have mounted long before
    // `isLive` flipped true, in which case the initial state is already stale.
    setNow(Date.now());

    const id = setInterval(() => setNow(Date.now()), LIVE_ELAPSED_TICK_MS);
    return () => clearInterval(id);
  }, [isLive, valid, startedMs]);

  if (!valid) return null;
  // Clamp: a start anchor in the future (clock skew, or a scheduled run that
  // slipped through) must not produce a negative duration.
  return Math.max(0, now - startedMs);
}

export default useLiveElapsed;
